import React, { useState, useContext, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { SidebarContext } from "context/SidebarContext";
import WhatsAppServices from "services/WhatsAppServices";
import DrawerButton from "components/form/DrawerButton";
import LabelArea from "components/form/LabelArea";
import Error from "components/form/Error";
import { notifyError, notifySuccess } from "utils/toast";

const EmailDrawer = () => {
  const { setIsUpdate, toggleDrawer } = useContext(SidebarContext);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  // ==================================================
  // FORM VALUES
  // ==================================================

  const messageType = watch("message_type");
  const category = watch("category");

  // ==================================================
  // DUMMY EMAIL TEMPLATES
  // ==================================================

  const [templates] = useState([
    {
      id: 1,
      name: "Booking Confirmation",
      file: "booking-confirmation.hbs",
      subject: "Your Booking Has Been Confirmed",
      language: "English",
      body: `
Hello {{customerName}},

Your booking {{bookingNumber}} has been
confirmed successfully.

Thank you for choosing our service.

Regards,
Admin Team
      `,
    },
    {
      id: 2,
      name: "Payment Reminder",
      file: "payment-reminder.hbs",
      subject: "Payment Reminder",
      language: "English",
      body: `
Hello {{customerName}},

This is a reminder that your payment
of {{amount}} is pending.

Please complete your payment.

Regards,
Accounts Team
      `,
    },
    {
      id: 3,
      name: "Booking Cancellation",
      file: "booking-cancellation.hbs",
      subject: "Your Booking Has Been Cancelled",
      language: "English",
      body: `
Hello {{customerName}},

Your booking {{bookingNumber}}
has been cancelled.

Regards,
Support Team
      `,
    },
    {
      id: 4,
      name: "Welcome Email",
      file: "welcome-email.hbs",
      subject: "Welcome to Our Service",
      language: "English",
      body: `
Hello {{customerName}},

Welcome to our service.

We are happy to have you with us.

Regards,
Admin Team
      `,
    },
    {
      id: 5,
      name: "Trip Completed",
      file: "trip-completed.hbs",
      subject: "Your Trip Has Been Completed",
      language: "English",
      body: `
Hello {{customerName}},

Your trip {{bookingNumber}}
has been completed successfully.

Thank you.

Regards,
Operations Team
      `,
    },
  ]);

  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // ==================================================
  // DUMMY EMAIL DATA
  // ==================================================

  const [emailList] = useState([
    // -----------------------------------------------
    // AGENTS
    // -----------------------------------------------

    {
      id: 1,
      name: "Kannan Agent",
      email: "kannan.agent@gmail.com",
      category: "agent",
    },
    {
      id: 2,
      name: "Arun Agent",
      email: "arun.agent@gmail.com",
      category: "agent",
    },
    {
      id: 3,
      name: "Vijay Agent",
      email: "vijay.agent@gmail.com",
      category: "agent",
    },

    // -----------------------------------------------
    // FRANCHISE
    // -----------------------------------------------

    {
      id: 4,
      name: "Chennai Franchise",
      email: "chennai.franchise@gmail.com",
      category: "franchise",
    },
    {
      id: 5,
      name: "Coimbatore Franchise",
      email: "coimbatore.franchise@gmail.com",
      category: "franchise",
    },
    {
      id: 6,
      name: "Madurai Franchise",
      email: "madurai.franchise@gmail.com",
      category: "franchise",
    },

    // -----------------------------------------------
    // VENDORS
    // -----------------------------------------------

    {
      id: 7,
      name: "ABC Travels",
      email: "abc.vendor@gmail.com",
      category: "vendor",
    },
    {
      id: 8,
      name: "XYZ Travels",
      email: "xyz.vendor@gmail.com",
      category: "vendor",
    },
    {
      id: 9,
      name: "City Travels",
      email: "city.vendor@gmail.com",
      category: "vendor",
    },
  ]);

  // ==================================================
  // SINGLE EMAIL
  // ==================================================

  const [selectedSingleEmail, setSelectedSingleEmail] = useState("");

  // ==================================================
  // BULK EMAILS
  // ==================================================

  const [selectedEmails, setSelectedEmails] = useState([]);

  // ==================================================
  // EMAIL DROPDOWN
  // ==================================================

  const [isEmailDropdownOpen, setIsEmailDropdownOpen] = useState(false);

  const emailDropdownRef = useRef(null);

  // ==================================================
  // CATEGORY EMAILS
  // ==================================================

  const categoryEmails = emailList.filter((item) => item.category === category);

  // ==================================================
  // OUTSIDE CLICK
  // ==================================================

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        emailDropdownRef.current &&
        !emailDropdownRef.current.contains(event.target)
      ) {
        setIsEmailDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // ==================================================
  // CATEGORY CHANGE
  // ==================================================

  useEffect(() => {
    if (!category) {
      setSelectedSingleEmail("");
      setSelectedEmails([]);
      setValue("to", "");
      setIsEmailDropdownOpen(false);

      return;
    }

    // -----------------------------------------------
    // BULK
    // Automatically select all category emails
    // -----------------------------------------------

    if (messageType === "bulk") {
      const emails = emailList
        .filter((item) => item.category === category)
        .map((item) => item.email);

      setSelectedEmails(emails);

      setSelectedSingleEmail("");
      setValue("to", "");
    }

    // -----------------------------------------------
    // SINGLE
    // -----------------------------------------------

    if (messageType === "single") {
      setSelectedEmails([]);
      setSelectedSingleEmail("");
      setValue("to", "");
    }

    setIsEmailDropdownOpen(false);
  }, [category, messageType, emailList, setValue]);

  // ==================================================
  // MESSAGE TYPE CHANGE
  // ==================================================

  const handleMessageTypeChange = (e) => {
    const type = e.target.value;

    // -----------------------------------------------
    // SINGLE
    // -----------------------------------------------

    if (type === "single") {
      setSelectedEmails([]);
      setSelectedSingleEmail("");
      setValue("to", "");
      setIsEmailDropdownOpen(false);

      return;
    }

    // -----------------------------------------------
    // BULK
    // -----------------------------------------------

    if (type === "bulk") {
      setSelectedSingleEmail("");
      setValue("to", "");

      if (category) {
        const emails = emailList
          .filter((item) => item.category === category)
          .map((item) => item.email);

        setSelectedEmails(emails);
      } else {
        setSelectedEmails([]);
      }

      setIsEmailDropdownOpen(false);
    }
  };

  // ==================================================
  // SINGLE EMAIL SELECT
  // ==================================================

  const handleSingleEmailSelect = (email) => {
    setSelectedSingleEmail(email);

    setValue("to", email, {
      shouldValidate: true,
      shouldDirty: true,
    });

    setIsEmailDropdownOpen(false);
  };

  // ==================================================
  // BULK EMAIL TOGGLE
  // ==================================================

  const handleToggleEmail = (email) => {
    if (selectedEmails.includes(email)) {
      setSelectedEmails((prev) =>
        prev.filter((selectedEmail) => selectedEmail !== email),
      );
    } else {
      setSelectedEmails((prev) => [...prev, email]);
    }
  };

  const handleRemoveEmail = (email) => {
    setSelectedEmails((prev) =>
      prev.filter((selectedEmail) => selectedEmail !== email),
    );
  };


  const handleClearAllEmails = () => {
    setSelectedEmails([]);
  };

  const handleClearSingleEmail = () => {
    setSelectedSingleEmail("");
    setValue("to", "");
  };

  const handleTemplateChange = (templateId) => {
    const template = templates.find(
      (item) => String(item.id) === String(templateId),
    );

    setSelectedTemplate(template || null);
  };

  const onSubmit = async (data) => {

    if (!selectedTemplate) {
      notifyError("Please select an email template");

      return;
    }


    let recipient;

    if (data.message_type === "single") {
      recipient = selectedSingleEmail || data.to;

      if (!recipient) {
        notifyError("Please select an email");

        return;
      }
    }

    if (data.message_type === "bulk") {
      recipient = selectedEmails;

      if (!selectedEmails || selectedEmails.length === 0) {
        notifyError("Please select at least one email");

        return;
      }
    }

    const payload = {
      messaging_product: "email",

      message_type: data.message_type,

      to: recipient,

      category: data.category,

      type: "template",

      template_id: selectedTemplate.id,

      template_name: selectedTemplate.name,

      template_file: selectedTemplate.file,

      content: {
        name: selectedTemplate.name,

        subject: selectedTemplate.subject,

        language: selectedTemplate.language,

        body: selectedTemplate.body,
      },
    };

    try {
      console.log("EMAIL PAYLOAD:", payload);

      await WhatsAppServices.sendMessage(payload);

      notifySuccess("Email sent successfully");

      toggleDrawer();

      setIsUpdate(true);
    } catch (err) {
      console.error("Email send failed:", err);

      notifyError(err.response?.data?.message || "Failed to send email");
    }
  };


  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-6">
      <LabelArea required label="Message Type" className="mt-4" />

      <select
        {...register("message_type", {
          required: "Message type is required",

          onChange: handleMessageTypeChange,
        })}
        className="w-full px-3 py-2 border rounded"
      >
        <option value="">Select Type</option>

        <option value="single">Single</option>

        <option value="bulk">Bulk</option>
      </select>

      <Error errorName={errors.message_type} />

      <LabelArea required label="Category" className="mt-4" />

      <select
        {...register("category", {
          required: "Category is required",
        })}
        className="w-full px-3 py-2 border rounded"
      >
        <option value="">Select Category</option>

        <option value="agent">Agent</option>

        <option value="franchise">Franchise</option>

        <option value="vendor">Vendor</option>
      </select>

      <Error errorName={errors.category} />

    
      {(messageType === "single" || messageType === "bulk") && (
        <div className="mt-4" ref={emailDropdownRef}>
          <LabelArea
            required
            label={`Select ${
              category
                ? category === "agent"
                  ? "Agent"
                  : category === "franchise"
                    ? "Franchise"
                    : "Vendor"
                : ""
            } Email${messageType === "bulk" ? "s" : ""}`}
          />

          <div className="relative">


            <div
              onClick={() => setIsEmailDropdownOpen((prev) => !prev)}
              className={`min-h-[42px] w-full border rounded bg-white px-2 py-1 flex items-center cursor-pointer ${
                errors.to ? "border-red-500" : "border-gray-300"
              }`}
            >
  
              <div className="flex flex-wrap items-center gap-1 flex-1">


                {messageType === "single" && selectedSingleEmail && (
                  <div
                    className="inline-flex items-center gap-1 bg-green-600 text-white rounded-full px-3 py-2 text-xs"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span>
                      {
                        categoryEmails.find(
                          (item) => item.email === selectedSingleEmail,
                        )?.name
                      }
                    </span>

                    <button
                      type="button"
                      onClick={handleClearSingleEmail}
                      className="ml-1 text-white font-bold text-sm leading-none hover:text-gray-200"
                    >
                      ×
                    </button>
                  </div>
                )}


                {messageType === "bulk" &&
                  selectedEmails.map((email) => {
                    const user = categoryEmails.find(
                      (item) => item.email === email,
                    );

                    return (
                      <div
                        key={email}
                        className="inline-flex items-center gap-1 bg-green-600 text-white rounded-full px-3 py-2 text-xs"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span>{user?.name || email}</span>

                        <button
                          type="button"
                          onClick={() => handleRemoveEmail(email)}
                          className="ml-1 text-white font-bold text-sm leading-none hover:text-gray-200"
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}

  
                {((messageType === "single" && !selectedSingleEmail) ||
                  (messageType === "bulk" && selectedEmails.length === 0)) && (
                  <span className="text-sm text-gray-400 px-1">
                    {category
                      ? `Select ${
                          category === "agent"
                            ? "agent"
                            : category === "franchise"
                              ? "franchise"
                              : "vendor"
                        } email${messageType === "bulk" ? "s" : ""}`
                      : "Select category first"}
                  </span>
                )}
              </div>


              <div className="flex items-center gap-2 ml-2">
        
                {messageType === "single" && selectedSingleEmail && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();

                      handleClearSingleEmail();
                    }}
                    className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                  >
                    ×
                  </button>
                )}

  

                {messageType === "bulk" && selectedEmails.length > 0 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();

                      handleClearAllEmails();
                    }}
                    className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                  >
                    ×
                  </button>
                )}

                <div className="h-5 w-px bg-gray-300" />

                {/* Arrow */}

                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 text-gray-400 transition-transform ${
                    isEmailDropdownOpen ? "rotate-180" : ""
                  }`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>


            {isEmailDropdownOpen && (
              <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto">
              
                {!category ? (
                  <div className="px-3 py-3 text-sm text-gray-500">
                    Please select a category first
                  </div>
                ) : categoryEmails.length === 0 ? (
                  <div className="px-3 py-3 text-sm text-gray-500">
                    No emails available
                  </div>
                ) : (
                  categoryEmails.map((item) => {
                 
                    const isSelected =
                      messageType === "single"
                        ? selectedSingleEmail === item.email
                        : selectedEmails.includes(item.email);

                    return (
                      <div
                        key={item.id}
                        onClick={() => {
                      

                          if (messageType === "single") {
                            handleSingleEmailSelect(item.email);

                            return;
                          }

                       

                          handleToggleEmail(item.email);
                        }}
                        className={`flex items-center justify-between px-3 py-2.5 cursor-pointer transition ${
                          isSelected
                            ? "bg-green-600 text-white"
                            : "hover:bg-gray-50"
                        }`}
                      >
                      

                        <div className="flex flex-col">
                          <span
                            className={`text-sm font-medium ${
                              isSelected ? "text-white" : "text-gray-700"
                            }`}
                          >
                            {item.name}
                          </span>

                          <span
                            className={`text-xs ${
                              isSelected ? "text-white" : "text-gray-500"
                            }`}
                          >
                            {item.email}
                          </span>
                        </div>

                     
                        {isSelected && (
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-white"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M16.704 5.29a1 1 0 010 1.42l-7.25 7.25a1 1 0 01-1.415 0l-3.25-3.25a1 1 0 111.415 1.42l2.543 2.544 6.543-6.544a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

    
          {messageType === "bulk" && selectedEmails.length > 0 && (
            <div className="mt-1 text-xs text-gray-500">
              {selectedEmails.length} email
              {selectedEmails.length > 1 ? "s" : ""} selected
            </div>
          )}

          {messageType === "single" && (
            <input
              type="hidden"
              {...register("to", {
                required: "Recipient email is required",

                validate: (value) => {
                  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value || "")) {
                    return "Please select a valid email address";
                  }

                  return true;
                },
              })}
            />
          )}

          {messageType === "single" && <Error errorName={errors.to} />}
        </div>
      )}

     
      <LabelArea required label="Select Email Template" className="mt-4" />

      <select
        className="w-full px-3 py-2 border rounded"
        {...register("template", {
          required: "Template is required",

          onChange: (e) => handleTemplateChange(e.target.value),
        })}
      >
        <option value="">Select Email Template</option>

        {templates.map((template) => (
          <option key={template.id} value={template.id}>
            {template.name}
          </option>
        ))}
      </select>

      <Error errorName={errors.template} />

      {/* ================================================= */}
      {/* EMAIL TEMPLATE PREVIEW */}
      {/* ================================================= */}

      {selectedTemplate && (
        <div className="mt-4">
          <LabelArea label="Email Template Preview" className="mt-4" />

          <div className="border rounded bg-gray-50 p-4">
            {/* --------------------------------------------- */}
            {/* SUBJECT */}
            {/* --------------------------------------------- */}

            <div className="mb-4">
              <div className="text-xs text-gray-500 mb-1">Subject</div>

              <div className="text-sm font-medium text-gray-700">
                {selectedTemplate.subject}
              </div>
            </div>

            {/* --------------------------------------------- */}
            {/* TEMPLATE FILE */}
            {/* --------------------------------------------- */}

            <div className="mb-4">
              <div className="text-xs text-gray-500 mb-1">Template File</div>

              <div className="text-sm text-gray-700">
                {selectedTemplate.file}
              </div>
            </div>

            {/* --------------------------------------------- */}
            {/* BODY */}
            {/* --------------------------------------------- */}

            <div>
              <div className="text-xs text-gray-500 mb-1">Preview</div>

              <div className="bg-white border rounded p-4">
                <div className="text-sm text-gray-700 whitespace-pre-line">
                  {selectedTemplate.body}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================================================= */}
      {/* SEND */}
      {/* ================================================= */}

      <div className="mt-4">
        <DrawerButton title="Send" isSubmitting={false} />
      </div>
    </form>
  );
};

export default EmailDrawer;
